// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for managing event groups in asset timelines.
 * This module provides a comprehensive interface for creating and
 * editing event groups, such as shipments or maintenance routines,
 * with support for complex validation, date constraints, and
 * property management.
 *
 * Key features:
 * - Timeline visualization
 * - Date validation
 * - Property management
 * - Location selection
 * - Carrier tracking
 * - Event sequencing
 * - Error handling
 * - Form validation
 */
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { hasIntersectionViolationError } from '@/api/error-utils';
import {
  CreateEventGroupParameters,
  CreateEventGroupProperty,
  createEventGroup,
} from '@/asset/api/create-event-group';
import { deleteEventGroup } from '@/asset/api/delete-event-group';
import {
  Asset,
  EventConfiguration,
  EventGroup,
  EventGroupConfiguration,
} from '@/asset/api/types';
import { updateEventGroup } from '@/asset/api/update-event-group';
import { AssetInventoryDateWarning } from '@/asset/components/asset-inventory-date-warning';
import {
  AssetTimeline,
  SHIPMENT_SEGMENT_INPUT_ID,
} from '@/asset/components/asset-timeline';
import { AssetTrackingHistory } from '@/asset/components/asset-tracking-history';
import { FormShippingCarrierTrackingLabel } from '@/asset/components/form-shipping-carrier-tracking-label';
import { useEventGroups } from '@/asset/hooks/use-event-groups';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormDateTimePicker } from '@/components/form-date-time-picker';
import { FormShippingCarrierSearch } from '@/components/form-shipping-carrier-search';
import { FormTextField } from '@/components/form-text-field';
import { LoadingIndicator } from '@/components/navigation-layout';
import { SupportSnackbar } from '@/components/support-snackbar';
import { TimelineControls } from '@/components/timeline-controls';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import { WarningAmberRounded } from '@/constants/hardfin-standard-icons';
import { shippingCarrierSchema } from '@/constants/shipping-carrier';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTimelineState } from '@/hooks/use-timeline-state';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import {
  SparseLocationWithParent,
  sparseLocationWithParentSchema,
} from '@/sparse/api/types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Suspense, useEffect, useRef } from 'react';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for text-based event properties.
 * Validates simple text input fields within events.
 *
 * @example
 * ```ts
 * const textProperty = {
 *   id: 'prop-123',
 *   type: 'TEXT',
 *   label: 'Notes',
 *   value: 'Maintenance completed successfully'
 * };
 * ```
 */
const textPropertySchema = z.object({
  id: z.string(),
  type: z.literal('TEXT'),
  label: z.string(),
  value: z.string(),
});

/**
 * Schema for carrier tracking values within events.
 * Validates shipping carrier and tracking number combinations.
 *
 * @example
 * ```ts
 * const trackingValue = {
 *   name: 'Package 1',
 *   trackingNumber: '1Z999AA1234567890',
 *   carrier: { id: 'ups', name: 'UPS' }
 * };
 * ```
 */
const carrierTrackingValueSchema = z.object({
  name: z.string(),
  trackingNumber: z.string(),
  carrier: shippingCarrierSchema.nullable(),
});

/**
 * Schema for carrier tracking properties.
 * Combines carrier tracking values with property metadata.
 *
 * @example
 * ```ts
 * const trackingProperty = {
 *   id: 'track-123',
 *   type: 'CARRIER_TRACKING',
 *   label: 'Shipping Details',
 *   value: {
 *     name: 'Package 1',
 *     trackingNumber: '1Z999AA1234567890',
 *     carrier: { id: 'ups', name: 'UPS' }
 *   }
 * };
 * ```
 */
const carrierTrackingSchema = z.object({
  id: z.string(),
  type: z.literal('CARRIER_TRACKING'),
  label: z.string(),
  value: carrierTrackingValueSchema,
});

/**
 * Type definition for carrier tracking value data.
 * Used for managing shipping information in events.
 *
 * @typedef {Object} CarrierTrackingValue
 */
type CarrierTrackingValue = z.infer<typeof carrierTrackingValueSchema>;

/**
 * Schema for all possible event property types.
 * Combines text and carrier tracking schemas.
 *
 * @example
 * ```ts
 * const property = {
 *   id: 'prop-123',
 *   type: 'TEXT',
 *   label: 'Notes',
 *   value: 'Maintenance completed successfully'
 * };
 * ```
 */
const eventPropertySchema = z.union([
  textPropertySchema,
  carrierTrackingSchema,
]);

/**
 * Type definition for event property data.
 * Used for managing different types of event metadata.
 *
 * @typedef {Object} EventProperty
 */
type EventProperty = z.infer<typeof eventPropertySchema>;

/**
 * Calculates the midpoint between two dates.
 * Used for positioning new events in the timeline.
 * Falls back to today if dates are invalid.
 *
 * @param start - The starting date
 * @param end - The ending date
 * @returns The calculated midpoint date
 *
 * @example
 * ```ts
 * const start = DateTime.fromISO('2024-01-01');
 * const end = DateTime.fromISO('2024-01-05');
 * const mid = getMidpoint(start, end);
 * // mid = 2024-01-03T00:00:00
 * ```
 */
function getMidpoint(start: DateTime, end: DateTime) {
  if (start > end) {
    return DateTime.now().startOf('day');
  }

  const intervals = start.until(end).divideEqually(2);

  if (intervals.length === 0) {
    return DateTime.now().startOf('day');
  }

  return intervals[0].end.startOf('day');
}

/**
 * Determines the default date for a new or existing event group.
 * Uses the midpoint of the event group's date range if available,
 * otherwise defaults to today.
 *
 * @param eventGroup - Optional existing event group
 * @returns The calculated default date
 *
 * @example
 * ```ts
 * const group = {
 *   minimumDate: DateTime.fromISO('2024-01-01'),
 *   maximumDate: DateTime.fromISO('2024-01-05')
 * };
 * const date = getDateTime(group);
 * // date = 2024-01-03T00:00:00
 * ```
 */
function getDateTime(eventGroup: EventGroup | undefined) {
  if (eventGroup === undefined) {
    return DateTime.now().startOf('day');
  }

  if (eventGroup.minimumDate && eventGroup.maximumDate) {
    return getMidpoint(eventGroup.minimumDate, eventGroup.maximumDate);
  }

  return DateTime.now().startOf('day');
}

/**
 * Validates the chronological order of events.
 * Ensures events occur in the correct sequence and
 * returns an error message if validation fails.
 *
 * @param events - Array of events to validate
 * @param configurations - Array of event configurations
 * @returns Error message if validation fails, null otherwise
 *
 * @example
 * ```ts
 * const events = [
 *   { occurredAt: '2024-01-01T00:00:00' },
 *   { occurredAt: '2024-01-02T00:00:00' }
 * ];
 * const configs = [
 *   { type: { name: 'Start' } },
 *   { type: { name: 'End' } }
 * ];
 * const error = getDateTimeValidationError(events, configs);
 * // error = null (dates are in correct order)
 * ```
 */
function getDateTimeValidationError(
  events: InputData['events'],
  configurations: EventConfiguration[]
) {
  if (events.length === 0) {
    return null;
  }

  let currentName: string | null = null;
  let currentDateTime: DateTime | null = null;

  for (const [index, event] of events.entries()) {
    if (event.occurredAt === null) {
      continue;
    }

    if (currentDateTime === null) {
      currentDateTime = event.occurredAt;
      currentName = configurations[index].type.name;

      continue;
    }

    if (event.occurredAt < currentDateTime) {
      const name = configurations[index].type.name.toLowerCase();

      return `${currentName} time must be earlier than ${name} time`;
    }
  }
}

/**
 * Checks if an event property is empty.
 * Different property types have different
 * emptiness criteria.
 *
 * @param property - The property to check
 * @returns True if the property is empty
 *
 * @example
 * ```ts
 * const textProp = {
 *   type: 'TEXT',
 *   value: ''
 * };
 * const isEmpty = isEmptyProperty(textProp);
 * // isEmpty = true
 * ```
 */
function isEmptyProperty(property: EventProperty) {
  if (property.type === 'CARRIER_TRACKING') {
    return (
      property.value.trackingNumber === '' &&
      property.value.name === '' &&
      property.value.carrier === null
    );
  }

  return property.value === '';
}

/**
 * Interface for form input data.
 * Represents the complete state of the event group form.
 *
 * @interface InputData
 */
interface InputData {
  /**
   * Array of events in the group.
   * Each event contains timing and property information.
   */
  events: {
    /**
     * ISO timestamp when the event occurred.
     * Can be null if the event hasn't happened yet.
     */
    occurredAt: DateTime | null;

    /**
     * Expected date for the event.
     * Used for planning future events.
     */
    expectedAt: DateTime | null;

    /**
     * Promise date for the event.
     * Date-only value when event is promised to occur.
     */
    promiseDate: DateTime | null;

    /**
     * Array of properties associated with the event.
     * Can include text notes, tracking info, etc.
     */
    properties: EventProperty[];
  }[];

  /**
   * Starting location for the event group.
   * Required for validation and timeline display.
   */
  origin: SparseLocationWithParent | null;

  /**
   * Ending location for the event group.
   * Required for validation and timeline display.
   */
  destination: SparseLocationWithParent | null;
}

/**
 * Schema for validating form output data.
 * Enforces required fields, event sequencing, and minimum date restrictions.
 */
const outputSchema = z.object({
  origin: sparseLocationWithParentSchema
    .nullable()
    .refine((location) => location !== null, { message: 'Origin is required' }),
  destination: sparseLocationWithParentSchema
    .nullable()
    .refine((location) => location !== null, {
      message: 'Destination is required',
    }),
  events: z
    .array(
      z.object({
        occurredAt: dateTimeSchema.nullable().refine(
          (value) => {
            if (value === null) return true;
            return value >= MINIMUM_DATE;
          },
          {
            message: 'Date cannot be before January 1, 1970',
          }
        ),
        expectedAt: dateSchema.nullable().refine(
          (value) => {
            if (value === null) return true;
            return value >= MINIMUM_DATE;
          },
          {
            message: 'Date cannot be before January 1, 1970',
          }
        ),
        promiseDate: dateSchema.nullable().refine(
          (value) => {
            if (value === null) return true;
            return value >= MINIMUM_DATE;
          },
          {
            message: 'Date cannot be before January 1, 1970',
          }
        ),
        properties: z.array(eventPropertySchema),
      })
    )
    .refine((events) => {
      return events.reduce((isValid, event, index, events) => {
        if (index === events.length - 1) {
          return isValid;
        }

        const nextEvent = events[index + 1];

        // Only check occurredAt, ignore expectedAt
        const eventTimestamp = event.occurredAt;
        const nextEventTimestamp = nextEvent.occurredAt;

        // If either event has null occurredAt, skip validation (ignore future events)
        if (nextEventTimestamp === null || eventTimestamp === null) {
          return isValid;
        }

        return isValid && eventTimestamp <= nextEventTimestamp;
      }, true);
    }),
});

/**
 * Type definition for validated form output.
 * Used when submitting the form data.
 *
 * @typedef {Object} OutputData
 */
type OutputData = z.infer<typeof outputSchema>;

/**
 * Generates default values for the form.
 * Combines configuration defaults with existing
 * event group data if available.
 *
 * @param configuration - Event group configuration
 * @param asset - Asset data for defaults
 * @param eventGroup - Optional existing event group
 * @returns Default form values
 */
function getDefaultValues(
  configuration: EventGroupConfiguration,
  asset: Asset,
  eventGroup: EventGroup | undefined
): InputData {
  return {
    origin: eventGroup?.origin ?? asset.currentLocation,
    destination: eventGroup?.destination ?? null,
    events: configuration.events.map(({ properties, type }) => {
      const existingEvent = eventGroup?.events.find(
        (event) => event.type.id === type.id
      );
      const coalescedProperties = properties ?? [];

      // Convert the UTC datetime to the appropriate timezone:
      // - If eventTimezone is set, use that timezone
      // - Otherwise, use the browser's timezone
      let occurredAt = existingEvent?.occurredAt ?? null;
      if (occurredAt) {
        const browserTimeZone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;
        const targetTimezone = existingEvent?.eventTimezone ?? browserTimeZone;
        occurredAt = occurredAt.setZone(targetTimezone);
      }

      return {
        occurredAt,
        expectedAt: existingEvent?.expectedAt ?? null,
        promiseDate: existingEvent?.promiseDate ?? null,
        properties: coalescedProperties.map((property, index) => {
          if (property.type === 'CARRIER_TRACKING') {
            const value: CarrierTrackingValue = {
              trackingNumber: '',
              carrier: null,
              name: '',
            };

            if (existingEvent) {
              const existingProperty = existingEvent.properties[index];

              if (existingProperty.type === 'CARRIER_TRACKING') {
                if (existingProperty.value) {
                  value.name = existingProperty.value.name ?? '';
                  value.carrier = existingProperty.value.carrier;
                  value.trackingNumber =
                    existingProperty.value.trackingNumber ?? '';
                }
              }
            }

            return {
              value,
              id: property.id,
              type: 'CARRIER_TRACKING',
              label: property.label,
            };
          }

          let value = '';

          if (existingEvent) {
            const existingProperty = existingEvent.properties[index];

            if (existingProperty.type === 'TEXT') {
              value = existingProperty.value ?? '';
            }
          }

          return {
            value,
            id: property.id,
            type: 'TEXT',
            label: property.label,
          };
        }),
      };
    }),
  };
}

/**
 * Checks whether two locations refer to the same effective site.
 * Handles zone hierarchy: a zone matches its parent site, and sibling
 * zones within the same parent site also match.
 *
 * This function handles two distinct data shapes depending on the source:
 * - Form-sourced values (from the location picker via expandLocationsWithZones)
 *   carry the parent site's `zones` array directly on the option. Checks 2-3
 *   (a.zones / b.zones) handle this case.
 * - API-sourced values carry a nested `parentLocation` with its own `zones`
 *   array. Checks 4-5 (a.parentLocation.zones / b.parentLocation.zones)
 *   handle this case.
 */
function locationsMatch(
  a: SparseLocationWithParent,
  b: SparseLocationWithParent
): boolean {
  if (a.id === b.id) {
    return true;
  }

  // Check if either location's zones list contains the other
  if (a.zones.some((zone) => zone.id === b.id)) {
    return true;
  }

  if (b.zones.some((zone) => zone.id === a.id)) {
    return true;
  }

  // Check parent location zones (available on API-sourced data)
  if (a.parentLocation?.zones.some((zone) => zone.id === b.id)) {
    return true;
  }

  if (b.parentLocation?.zones.some((zone) => zone.id === a.id)) {
    return true;
  }

  return false;
}

/**
 * Props for configuring the EventGroupDialog component.
 *
 * @interface Props
 */
interface Props {
  /**
   * If true, the dialog is open.
   */
  isOpen: boolean;

  /**
   * The asset to move.
   */
  asset: Asset;

  /**
   * If provided, the event group will be updated instead of created.
   */
  eventGroup?: EventGroup;

  /**
   * The configuration for the event group.
   */
  configuration: EventGroupConfiguration;

  /**
   * A callback fired when the dialog requests to be closed.
   */
  onClose: () => void;
}

/**
 * A dialog component for creating a new asset event group.
 */
export function EventGroupDialog(props: Props) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const deleteConfirmationDialog = useDisclosure();
  const { setDateTime, setZoom, ...timelineState } = useTimelineState(
    // The 'md' breakpoint is the width of the dialog.
    theme.breakpoints.values.md,
    'week',
    getDateTime(props.eventGroup)
  );
  const createEventGroupMutation = useMutation({
    mutationFn: (parameters: CreateEventGroupParameters) =>
      createEventGroup(props.asset.key, props.configuration.id, parameters),
    onSuccess: async ({ eventGroup }) => {
      const values = getDefaultValues(
        props.configuration,
        props.asset,
        eventGroup
      );

      await queryClient.invalidateQueries({
        queryKey: ['asset', props.asset.key],
      });
      reset(values);
      resetAndClose();
    },
  });

  const updateEventGroupMutation = useMutation({
    mutationFn: (parameters: CreateEventGroupParameters) => {
      if (props.eventGroup === undefined) {
        throw new Error('An event group must be provided to update');
      }

      return updateEventGroup(
        props.asset.key,
        props.eventGroup.key,
        parameters
      );
    },
    onSuccess: async ({ eventGroup }) => {
      const values = getDefaultValues(
        props.configuration,
        props.asset,
        eventGroup
      );

      await queryClient.invalidateQueries({
        queryKey: ['asset', props.asset.key],
      });
      reset(values);
      resetAndClose();
    },
  });

  const deleteEventGroupMutation = useMutation({
    mutationFn: () => {
      if (props.eventGroup === undefined) {
        throw new Error('An event group must be provided to delete');
      }

      return deleteEventGroup(props.asset.key, props.eventGroup.key);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['asset', props.asset.key],
      });
      resetAndClose();
    },
  });

  const { control, formState, reset, handleSubmit } = useForm<
    InputData,
    unknown,
    OutputData
  >({
    resolver: zodResolver(outputSchema),
    defaultValues: getDefaultValues(
      props.configuration,
      props.asset,
      props.eventGroup
    ),
  });

  // Reset form values only on the false→true transition of isOpen, not on prop reference changes while open
  const wasOpen = useRef(false);
  useEffect(() => {
    if (props.isOpen && !wasOpen.current) {
      reset(
        getDefaultValues(props.configuration, props.asset, props.eventGroup)
      );
    }
    wasOpen.current = props.isOpen;
  }, [props.isOpen, props.asset, props.configuration, props.eventGroup, reset]);

  const { fields } = useFieldArray({ control, name: 'events' });

  // Check if this event group is linked to a shipment (disable editing if so)
  const isMutating =
    createEventGroupMutation.isPending ||
    updateEventGroupMutation.isPending ||
    deleteEventGroupMutation.isPending;
  const isLinkedToShipment = Boolean(props.eventGroup?.shipment);

  // Ensure that this has shipping data actually set, this is done by looking at the carrier to see if it is set to a value.
  const hasShippingData = fields.some((field) => {
    return field.properties.some(
      (property) =>
        property.type === 'CARRIER_TRACKING' &&
        typeof property.value === 'object' &&
        // Ensure a carrier and tracking number is set
        property.value.carrier &&
        property.value.carrier.length > 0 &&
        // Ensure the trackingNumber isn't empty
        property.value.trackingNumber &&
        property.value.trackingNumber.length > 0
    );
  });
  const destination = useWatch({ control, name: 'destination' });
  const origin = useWatch({ control, name: 'origin' });
  const events = useWatch({ control, name: 'events' });

  const inputStart = events.length === 0 ? null : events[0].occurredAt;

  const inputEnd =
    events.length === 0 ? null : events[events.length - 1].occurredAt;

  const expectedInputStart = events.length === 0 ? null : events[0].expectedAt;

  const expectedInputEnd =
    events.length === 0 ? null : events[events.length - 1].expectedAt;

  // Handle time range zoom selection if start/end change their values
  useEffect(() => {
    if (inputStart === null || inputEnd === null) {
      return;
    }

    const midpoint = getMidpoint(inputStart, inputEnd);

    setDateTime(midpoint);
    setZoom('week');
  }, [inputStart, inputEnd, setZoom, setDateTime]);

  const eventGroupsQuery = useEventGroups(
    props.asset.key,
    timelineState.interval,
    props.isOpen
  );

  const shouldUpdate = formState.isDirty;
  const isCreateAlertOpen =
    createEventGroupMutation.isError &&
    !hasIntersectionViolationError(createEventGroupMutation.error) &&
    !isPlanLimitError(createEventGroupMutation.error);

  const isUpdateAlertOpen =
    updateEventGroupMutation.isError &&
    !hasIntersectionViolationError(updateEventGroupMutation.error);

  const isIntersectionAlertOpen =
    (createEventGroupMutation.isError &&
      hasIntersectionViolationError(createEventGroupMutation.error)) ||
    (updateEventGroupMutation.isError &&
      hasIntersectionViolationError(updateEventGroupMutation.error));

  const eventGroups =
    eventGroupsQuery.data?.eventGroups.filter((eventGroup) => {
      if (props.eventGroup === undefined) {
        return true;
      }

      return eventGroup.id !== props.eventGroup.id;
    }) ?? [];

  const segments = [...eventGroups];

  const previousEventGroup =
    eventGroups.find((eventGroup) => {
      if (eventGroup.maximumDate === null) {
        return false;
      }

      if (inputStart === null) {
        return false;
      }

      return eventGroup.maximumDate <= inputStart;
    }) ?? null;

  const isInvalidOrigin =
    previousEventGroup === null ||
    origin === null ||
    previousEventGroup.destination === null
      ? false
      : !locationsMatch(previousEventGroup.destination, origin);

  const nextEventGroup =
    eventGroups
      .slice()
      .reverse()
      .find((eventGroup) => {
        if (eventGroup.minimumDate === null) {
          return false;
        }

        if (inputEnd === null) {
          return false;
        }

        return eventGroup.minimumDate >= inputEnd;
      }) ?? null;

  const isInvalidDestination =
    nextEventGroup === null ||
    destination === null ||
    nextEventGroup.origin === null
      ? false
      : !locationsMatch(destination, nextEventGroup.origin);

  const isInvalidInterval = segments.some((segment) => {
    if (segment.minimumDate === null || segment.maximumDate === null) {
      return false;
    }

    if (inputStart === null || inputEnd === null) {
      return false;
    }

    try {
      const segmentInterval = segment.minimumDate.until(segment.maximumDate);
      const inputInterval = inputStart.until(inputEnd);
      return segmentInterval.overlaps(inputInterval);
    } catch (_) {
      return false;
    }
  });

  const startDateTime = inputStart ?? expectedInputStart;
  const endDateTime = inputEnd ?? expectedInputEnd;

  if (startDateTime && endDateTime && startDateTime < endDateTime) {
    segments.unshift({
      origin,
      destination,
      shipment: null,
      id: SHIPMENT_SEGMENT_INPUT_ID,
      key: SHIPMENT_SEGMENT_INPUT_ID,
      minimumDate: startDateTime,
      maximumDate: endDateTime,
      configuration: props.configuration,
      events: events.map((event, index) => {
        const configuration = props.configuration.events[index];

        return {
          id: index.toString(),
          key: index.toString(),
          type: configuration.type,
          sequence: index,
          isRequired: false,
          label: configuration.type.name,
          properties: [],
          createdAt: DateTime.now(),
          occurredAt: event.occurredAt ?? null,
          expectedAt: event.expectedAt,
          promiseDate: event.promiseDate,
        };
      }),
    });
  }

  segments.sort((segment, other) => {
    const segmentDateTime = segment.minimumDate ?? segment.maximumDate;
    const otherDateTime = other.minimumDate ?? other.maximumDate;

    if (segmentDateTime === null || otherDateTime === null) {
      return 0;
    }

    return segmentDateTime.toMillis() - otherDateTime.toMillis();
  });

  const isTimelineOpen = events.some(
    (event) => event.occurredAt !== null || event.expectedAt !== null
  );

  const dateTimeValidationError = getDateTimeValidationError(
    events,
    props.configuration.events
  );

  const isDateTimeAlertOpen = Boolean(dateTimeValidationError);
  const isLocationAlertOpen =
    origin !== null && destination !== null && origin.id === destination.id;

  const isLocationWarningOpen =
    (isInvalidOrigin || isInvalidDestination) && !isInvalidInterval;

  const handleValidSubmission: SubmitHandler<OutputData> = (data) => {
    if (isInvalidInterval) {
      return;
    }
    const events = data.events.map((event, index) => {
      const properties = event.properties.reduce<CreateEventGroupProperty[]>(
        (fields, property) => {
          if (isEmptyProperty(property)) {
            return fields;
          }

          return [
            ...fields,
            { propertyId: property.id, value: property.value },
          ];
        },
        []
      );

      const eventTimezone = event.occurredAt?.zoneName ?? null;
      return {
        properties,
        typeId: props.configuration.events[index].type.id,
        eventTimezone: eventTimezone,
        occurredAt: event.occurredAt === null ? null : event.occurredAt.toUTC(),
        expectedAt: event.expectedAt,
        promiseDate: event.promiseDate,
      };
    });

    if (props.eventGroup === undefined) {
      return createEventGroupMutation.mutate({
        events,
        originId: data.origin.id,
        destinationId: data.destination.id,
      });
    }

    if (shouldUpdate) {
      return updateEventGroupMutation.mutate({
        events,
        originId: data.origin.id,
        destinationId: data.destination.id,
      });
    }

    close();
  };

  const handleClickDelete = () => {
    deleteEventGroupMutation.mutate();
  };

  const resetAndClose = () => {
    props.onClose();
    createEventGroupMutation.reset();
    updateEventGroupMutation.reset();
    deleteEventGroupMutation.reset();
  };

  const close = () => {
    if (isMutating) return;
    resetAndClose();
  };

  const handleCreateLocation = (name: string) => {
    const url = getNewLocationURL(name);

    window.open(url, '_blank');
  };

  return (
    <Dialog
      fullWidth
      maxWidth={hasShippingData ? 'xl' : 'lg'}
      open={props.isOpen}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <Box
        display="grid"
        gridTemplateColumns={hasShippingData ? '2fr 1fr' : '1fr'}
        columnGap={4}
        alignItems="start"
        flexDirection={'column'}
        paddingX={3}
        paddingY={2}
      >
        <Box display="grid" gridColumn={1}>
          <Dialog
            fullWidth
            maxWidth="sm"
            open={deleteConfirmationDialog.isOpen}
            onClose={deleteConfirmationDialog.close}
          >
            <DialogTitle>Delete move</DialogTitle>
            <DialogContent dividers>
              <Typography marginBottom={1}>
                Are you sure you want to delete this move? The data will be
                permanently removed from the asset’s history.
              </Typography>
              <Typography>
                Note: your audit log will continue to show a record of the
                deleted move.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button size="small" onClick={deleteConfirmationDialog.close}>
                No, go back
              </Button>
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={handleClickDelete}
                disabled={deleteEventGroupMutation.isPending}
              >
                Yes, delete
              </Button>
            </DialogActions>
          </Dialog>
          <SupportSnackbar
            isOpen={deleteEventGroupMutation.isError}
            onClose={deleteEventGroupMutation.reset}
            message="An error occurred while deleting the asset move."
          />
          <SupportSnackbar
            isOpen={isCreateAlertOpen}
            onClose={createEventGroupMutation.reset}
            message="An error occurred while moving the asset."
          />
          <SupportSnackbar
            isOpen={isUpdateAlertOpen}
            onClose={updateEventGroupMutation.reset}
            message="An error occurred while updating the asset move."
          />
          <DialogTitle>Move asset</DialogTitle>
          <DialogContent dividers sx={{ padding: 0 }}>
            {props.eventGroup?.shipment && (
              <Alert severity="info" sx={{ marginX: 3, marginTop: 2 }}>
                This move is part of shipment{' '}
                <a
                  href={`/shipments?type=${props.eventGroup.shipment.shipmentType === 'INBOUND' ? 'inbound' : 'outbound'}&query=${props.eventGroup.shipment.shipmentNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {props.eventGroup.shipment.shipmentNumber}
                </a>{' '}
                and cannot be modified directly. To update this move, please
                update the shipment.
              </Alert>
            )}
            <Box display="grid">
              <Box
                gridTemplateColumns="1fr 1fr"
                gridTemplateRows="auto auto"
                alignContent="start"
                columnGap={4}
                rowGap={2}
                paddingX={3}
                paddingY={2}
                display={'grid'}
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle1">Origin</Typography>
                  <FormLocationSearch
                    name="origin"
                    label="Location"
                    control={control}
                    onCreate={handleCreateLocation}
                    disabled={isLinkedToShipment}
                  />
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle1">Destination</Typography>
                  <FormLocationSearch
                    name="destination"
                    label="Location"
                    control={control}
                    onCreate={handleCreateLocation}
                    disabled={isLinkedToShipment}
                  />
                </Stack>
                <Collapse
                  in={isLocationAlertOpen}
                  sx={{ gridColumn: 'span 2' }}
                >
                  <Alert severity="warning" icon={<WarningAmberRounded />}>
                    Warning: origin and destination locations are the same
                  </Alert>
                </Collapse>
                {fields.map((field, index) => {
                  const occurredAt = events[index].occurredAt;

                  const expectedAt = events[index].expectedAt;
                  const promiseDate = events[index].promiseDate;

                  return (
                    <Stack key={field.id} spacing={2}>
                      <Typography variant="subtitle1">
                        {props.configuration.events[index].type.name}
                      </Typography>
                      <Stack>
                        <Stack direction="row" alignItems="center">
                          <FormDatePicker
                            name={`events.${index}.promiseDate`}
                            label="Promise date"
                            control={control}
                            isDisabled={isLinkedToShipment}
                          />
                          <AssetInventoryDateWarning
                            asset={props.asset}
                            dateTime={promiseDate}
                          />
                        </Stack>
                        <Stack
                          direction="row"
                          alignItems="center"
                          marginTop={2}
                        >
                          <FormDatePicker
                            name={`events.${index}.expectedAt`}
                            label="Expected date"
                            control={control}
                            isDisabled={isLinkedToShipment}
                          />
                          <AssetInventoryDateWarning
                            asset={props.asset}
                            dateTime={expectedAt}
                          />
                        </Stack>
                        <Stack
                          direction="row"
                          alignItems="center"
                          marginTop={2}
                        >
                          <FormDateTimePicker
                            disableFuture
                            name={`events.${index}.occurredAt`}
                            label="Actual date"
                            control={control}
                            isDisabled={isLinkedToShipment}
                          />
                          <AssetInventoryDateWarning
                            asset={props.asset}
                            dateTime={occurredAt}
                          />
                        </Stack>
                      </Stack>
                      {field.properties.map((propertyField, propertyIndex) => {
                        if (propertyField.type === 'CARRIER_TRACKING') {
                          const value =
                            events[index].properties[propertyIndex].value;
                          const isOther =
                            typeof value === 'object' &&
                            value.carrier === 'other';

                          return (
                            <Stack key={propertyField.id}>
                              <Stack spacing={2}>
                                <FormTextField
                                  name={`events.${index}.properties.${propertyIndex}.value.trackingNumber`}
                                  label="Tracking number"
                                  control={control}
                                  disabled={isLinkedToShipment}
                                />
                                <Stack>
                                  <FormShippingCarrierSearch
                                    name={`events.${index}.properties.${propertyIndex}.value.carrier`}
                                    label="Carrier"
                                    control={control}
                                    disabled={isLinkedToShipment}
                                  />
                                  <Collapse
                                    in={!isOther && !isLinkedToShipment}
                                    sx={{
                                      marginLeft: 'auto',
                                      marginRight: 2,
                                      paddingTop: 0.5,
                                    }}
                                  >
                                    <Controller
                                      name={`events.${index}.properties.${propertyIndex}.value.carrier`}
                                      control={control}
                                      render={({ field }) => {
                                        const handleClick = () => {
                                          field.onChange('other');
                                        };

                                        return (
                                          <Typography
                                            role="button"
                                            variant="body2"
                                            color="primary.main"
                                            onClick={handleClick}
                                            sx={{ cursor: 'pointer' }}
                                          >
                                            Enter manually
                                          </Typography>
                                        );
                                      }}
                                    />
                                  </Collapse>
                                </Stack>
                              </Stack>
                              <Collapse in={isOther} sx={{ paddingTop: 2 }}>
                                <FormTextField
                                  fullWidth
                                  name={`events.${index}.properties.${propertyIndex}.value.name`}
                                  label="Name"
                                  control={control}
                                  disabled={isLinkedToShipment}
                                />
                              </Collapse>
                              <Box paddingX={1}>
                                <FormShippingCarrierTrackingLabel
                                  control={control}
                                  name={`events.${index}.properties.${propertyIndex}`}
                                />
                              </Box>
                            </Stack>
                          );
                        }

                        return (
                          <FormTextField
                            key={propertyField.id}
                            name={`events.${index}.properties.${propertyIndex}.value`}
                            label={propertyField.label}
                            control={control}
                            disabled={isLinkedToShipment}
                          />
                        );
                      })}
                    </Stack>
                  );
                })}
                <Box gridColumn="span 2">
                  <Collapse in={isDateTimeAlertOpen}>
                    <Alert severity="error">{dateTimeValidationError}</Alert>
                  </Collapse>
                  <Collapse in={isInvalidInterval}>
                    <Alert severity="error">
                      This proposed move overlaps with an existing move for this
                      asset. Please adjust the date range or correct the dates
                      for the existing move.
                    </Alert>
                  </Collapse>
                  <Collapse in={isIntersectionAlertOpen}>
                    <Alert severity="error">
                      Delivery date can not be equal to the delivery date of the
                      previous move
                    </Alert>
                  </Collapse>
                  <Collapse in={isLocationWarningOpen}>
                    <Alert severity="warning" icon={<WarningAmberRounded />}>
                      Warning: moves may be missing
                      <ul style={{ margin: 0, padding: 16 }}>
                        {isInvalidOrigin && (
                          <li>
                            The asset was at{' '}
                            <b>
                              {previousEventGroup?.destination
                                ? getEffectiveLocationName(
                                    previousEventGroup.destination
                                  )
                                : null}
                            </b>{' '}
                            on{' '}
                            <b>
                              {previousEventGroup?.maximumDate?.toLocaleString({
                                dateStyle: 'medium',
                              })}
                            </b>{' '}
                            and is being recorded at{' '}
                            <b>
                              {origin ? getEffectiveLocationName(origin) : null}
                            </b>{' '}
                            on{' '}
                            <b>
                              {inputStart?.toLocaleString({
                                dateStyle: 'medium',
                              })}
                            </b>
                          </li>
                        )}
                        {isInvalidDestination && (
                          <li>
                            The asset is being recorded at{' '}
                            <b>
                              {destination
                                ? getEffectiveLocationName(destination)
                                : null}
                            </b>{' '}
                            on{' '}
                            <b>
                              {inputEnd?.toLocaleString({
                                dateStyle: 'medium',
                              })}
                            </b>{' '}
                            and will appear at{' '}
                            <b>
                              {nextEventGroup?.origin
                                ? getEffectiveLocationName(
                                    nextEventGroup.origin
                                  )
                                : null}
                            </b>{' '}
                            on{' '}
                            <b>
                              {nextEventGroup?.minimumDate?.toLocaleString({
                                dateStyle: 'medium',
                              })}
                            </b>
                          </li>
                        )}
                      </ul>
                    </Alert>
                  </Collapse>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <Collapse
            in={isTimelineOpen}
            sx={{ maxHeight: 300, overflow: 'auto' }}
          >
            <Box
              display="flex"
              justifyContent="end"
              padding={1}
              position="sticky"
              top={0}
              bgcolor={theme.palette.background.paper}
            >
              <TimelineControls
                density={timelineState.zoom}
                canZoomIn={timelineState.canZoomIn}
                canZoomOut={timelineState.canZoomOut}
                onClickZoomIn={timelineState.zoomIn}
                onClickZoomOut={timelineState.zoomOut}
                onClickForward={timelineState.shiftForward}
                onClickBackward={timelineState.shiftBackward}
              />
            </Box>
            <AssetTimeline
              asset={props.asset}
              density={timelineState.zoom}
              interval={timelineState.interval}
              shipments={segments}
            />
          </Collapse>
          <Divider />
          <DialogActions>
            {props.eventGroup && (
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={deleteConfirmationDialog.open}
                sx={{ marginRight: 'auto' }}
                disabled={isLinkedToShipment || isMutating}
              >
                Delete
              </Button>
            )}
            <Button size="small" onClick={close} disabled={isMutating}>
              Cancel
            </Button>
            <Button
              size="small"
              type="submit"
              color="secondary"
              variant="contained"
              disabled={isLinkedToShipment || isMutating}
            >
              {props.eventGroup === undefined ? 'Move' : 'Save'}
            </Button>
          </DialogActions>
        </Box>
        {hasShippingData && props.eventGroup && (
          <Box gridColumn={2} maxHeight="780px" overflow="auto" display="flex">
            <Divider flexItem orientation="vertical" variant="middle" />
            <Box display="flex" paddingX={3} paddingY={2} minWidth={260}>
              <Suspense fallback={<LoadingIndicator />}>
                <AssetTrackingHistory
                  assetKey={props.asset.key}
                  aegKey={props.eventGroup.key}
                />
              </Suspense>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
